import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import { useEchoTest } from '@useEchoTest';

import { getDevices, IVideoRoomTokenRequest } from 'vcs-common';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation, useGenerateVideoRoomTokenMutation } from '../../services/users.api';
import { ArrowLeft } from '../icons/arrow-left';
import Button from '../common/button';
import { CustomSelect } from '../common/select';

export const SettingsBlock: FC<{
  closeSettings: () => void;
}> = ({ closeSettings }) => {
  const localVideoRef = useRef<any>(null);
  const remoteVideoRef = useRef<any>(null);
  const userData = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [changeUser] = useChangeUserMutation();
  const [generateToken] = useGenerateVideoRoomTokenMutation();

  const [isDevicesInitialized, setIsDevicesInitialized] = useState(false);
  const [audioDevices, setAudioDevices] = useState<any[]>([]);
  const [videoDevices, setVideoDevices] = useState<any[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<any[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState<MediaDeviceInfo>();
  const [isAudioMuted, setIsAudioMuted] = useState(true);

  const generateRoomToken = useCallback(
    async (request: IVideoRoomTokenRequest) => {
      return await generateToken(request).unwrap();
    },
    [generateToken],
  );

  const { initialized, start, stop, changeAudioDevice, changeVideoDevice } = useEchoTest(
    localVideoRef,
    remoteVideoRef,
    generateRoomToken,
  );

  const stopEcho = useCallback(() => {
    console.debug('Stopping echo');
    stop();
    closeSettings();
  }, [closeSettings, stop]);

  useEffect(() => {
    console.debug('Starting echo effect');
    const startEcho = async () => {
      if (initialized && isDevicesInitialized && selectedVideoDevice) {
        console.debug(`Starting echo with init ${initialized} selected video ${selectedVideoDevice}`);

        const selectedVideoDeviceId = selectedVideoDevice
          ? videoDevices?.find((d) => d.label === selectedVideoDevice)?.deviceId
          : undefined;
        const selectedAudioDeviceId = selectedAudioDevice
          ? audioDevices?.find((d) => d.label === selectedAudioDevice)?.deviceId
          : undefined;

        await start(selectedVideoDeviceId, selectedAudioDeviceId);
      }
    };

    startEcho();
  }, [
    audioDevices,
    initialized,
    isDevicesInitialized,
    selectedAudioDevice,
    selectedVideoDevice,
    start,
    stop,
    videoDevices,
  ]);

  const saveSettings = async () => {
    try {
      const newUserData = await changeUser({
        id: userData?.id,
        settings: [
          {
            video: selectedVideoDevice,
            micro: selectedAudioDevice,
            speaker: selectedAudioOutputDevice?.label ?? '',
          },
        ],
      });
      dispatch(setUserData(newUserData?.data || userData));
      localStorage.setItem('video', selectedVideoDevice);
      localStorage.setItem('micro', selectedAudioDevice);
      localStorage.setItem('speaker', selectedAudioOutputDevice?.label ?? '');
      toast.success('Данные успешно сохранены');
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    const getDevicesAsync = async () => {
      console.debug('Init devices effect');

      if (initialized) {
        const { audios, videos, audioOutputs } = await getDevices();
        setAudioDevices(audios);
        setVideoDevices(videos);
        setAudioOutputDevices(audioOutputs);

        let selectedVideo: MediaDeviceInfo | undefined = undefined;
        let selectedAudio: MediaDeviceInfo | undefined = undefined;
        let selectedAudioOutput: MediaDeviceInfo | undefined = undefined;

        if (localStorage.getItem('video')) {
          selectedVideo = videos.find((el) => el.label === localStorage.getItem('video'));

          console.debug(`Found video device ${selectedVideo?.label} from localstorage`);
        }

        if (!selectedVideo && videos) {
          selectedVideo = videos[0];

          console.debug(`Saving video device ${selectedVideo?.label} to localstorage`);
          localStorage.setItem('video', selectedVideo.label);
        }

        if (selectedVideo) {
          console.debug(`Setting video device ${selectedVideo?.label}`);
          await changeVideoDevice(selectedVideo.deviceId);
          setSelectedVideoDevice(selectedVideo.label);
        }

        if (localStorage.getItem('micro')) {
          selectedAudio = audios.find((el) => el.label === localStorage.getItem('micro'));
          console.debug(`Found microphone ${selectedAudio?.label} from localstorage`);
        }

        if (!selectedAudio && audios) {
          selectedAudio = audios[0];
          console.debug(`Saving microphone device ${selectedAudio?.label} to localstorage`);
          localStorage.setItem('micro', selectedAudio.label);
        }

        if (selectedAudio) {
          console.debug(`Setting microphone device ${selectedAudio?.label}`);
          await changeAudioDevice(selectedAudio.deviceId);
          setSelectedAudioDevice(selectedAudio.label);
        }

        if (localStorage.getItem('speaker')) {
          selectedAudioOutput = audioOutputs.find((el) => el.label === localStorage.getItem('speaker'));
          console.debug(`Found speakers ${selectedAudioOutput?.label} from localstorage`);
        }

        if (!selectedAudioOutput && audioOutputs) {
          selectedAudioOutput = audioOutputs[0];
          console.debug(`Saving speakers ${selectedAudioOutput?.label} to localstorage`);
          localStorage.setItem('speaker', selectedAudioOutput.label);
        }

        if (selectedAudioOutput) {
          console.debug(`Setting speakers ${selectedAudioOutput?.label}`);
          setSelectedAudioOutputDevice((prevDevice) => {
            return prevDevice?.deviceId === selectedAudioOutput.deviceId ? prevDevice : selectedAudioOutput;
          });
        }

        setIsDevicesInitialized(true);
      }
    };

    getDevicesAsync();
  }, [changeAudioDevice, changeVideoDevice, initialized]);

  useEffect(() => {
    if (selectedAudioOutputDevice && remoteVideoRef?.current && remoteVideoRef?.current?.setSinkId) {
      console.debug(`Updating speakers to ${selectedAudioOutputDevice.label}`);
      remoteVideoRef?.current && remoteVideoRef?.current?.setSinkId(selectedAudioOutputDevice.deviceId);
    }
  }, [selectedAudioOutputDevice]);

  const toggleAudioMute = () => {
    setIsAudioMuted(!isAudioMuted);
  };

  return (
    <div className="flex flex-col w-full h-full pb-5 absolute bg-white w-full">
      <div className="w-full flex justify-between mb-[28px]">
        <div className="flex items-center gap-5">
          <button onClick={() => stopEcho()} className="group ">
            <ArrowLeft className="fill-redBase group-hover:fill-blackBase" />
          </button>
          <h1 className="font-comfortaa font-bold text-2xl">Настройки</h1>
        </div>
        <Button
          type={'black'}
          onClick={() => saveSettings()}
          className="w-max hidden !p-[10px_30px] !text-base !font-medium !leading-4 group sm:flex items-center gap-5"
        >
          Сохранить
        </Button>
      </div>
      <div className="w-full flex justify-between flex-wrap gap-5">
        <div className="w-full max-w-[400px] flex flex-col gap-3 mb-[28px] flex-wrap">
          <p className="font-comfortaa font-bold text-xl">Видео</p>

          <div className="w-full flex flex-col  gap-3">
            <video
              className="w-full  h-[220px] bg-[#EEEEEE] rounded-[20px]"
              id="settings-video"
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted={isAudioMuted}
            />

            <div className="flex flex-col w-full max-w-[400px] gap-5">
              <CustomSelect
                className="z-[15]"
                selected={selectedVideoDevice}
                onChange={async (a) => {
                  setSelectedVideoDevice(a);
                  const data = videoDevices.find((el) => el.label === a);
                  if (data) {
                    await changeVideoDevice(data.deviceId);
                  }
                }}
                items={videoDevices.map((device) => device.label || `Audio Input ${device.deviceId}`)}
                isChange={false}
                readOnly={true}
                clickToOpenClose={true}
              />
            </div>
          </div>
        </div>
        <div className="w-full max-w-[400px] flex flex-col gap-3">
          <p className="font-comfortaa font-bold text-xl">Аудио</p>

          <div className="w-full  flex justify-between gap-5 flex-wrap">
            <div className="flex flex-col w-full  gap-3">
              <CustomSelect
                className="z-[10]"
                selected={selectedAudioDevice}
                onChange={async (a) => {
                  setSelectedAudioDevice(a);
                  const data = audioDevices.find((el) => el.label === a);
                  if (data) {
                    await changeAudioDevice(data.deviceId);
                  }
                }}
                items={audioDevices.map((device) => device.label || `Audio Input ${device.deviceId}`)}
                isChange={false}
                readOnly={true}
                clickToOpenClose={true}
              />
              <CustomSelect
                className="z-[5]"
                selected={selectedAudioOutputDevice?.label}
                onChange={(a) => {
                  const audioOutput = audioOutputDevices.find((d) => d.label === a);

                  if (audioOutput) {
                    setSelectedAudioOutputDevice(audioOutput);
                  }
                }}
                items={audioOutputDevices.map((device) => device.label || `Audio Input ${device.deviceId}`)}
                isChange={false}
                readOnly={true}
                clickToOpenClose={true}
              />
              <Button type="grey" className="!py-[10px] !text-base font-semibold" onClick={toggleAudioMute}>
                {!isAudioMuted ? 'Говорите' : 'Проверить звук'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Button
        type={'black'}
        onClick={() => saveSettings()}
        className="w-full sm:hidden !p-[10px_30px] !text-base !font-medium !leading-4 group flex items-center gap-5 mt-[40px]"
      >
        Сохранить
      </Button>
    </div>
  );
};
